import { useCallback, useMemo } from "react";
import { useCookies } from "react-cookie";
import { getUserIdFromStorage } from "shared/storageUtils";

export const PENDENCY_COOKIE_NAME = "ftvnp";
export const VIEWED_NOTIFICATION_COOKIE_NAME = "ftvnvl";

type CookieKeys =
  | typeof PENDENCY_COOKIE_NAME
  | typeof VIEWED_NOTIFICATION_COOKIE_NAME;

type Cookies = {
  [key in CookieKeys]?: string[];
};

export function useNotificationCookies() {
  const [cookies, setCookie] = useCookies<CookieKeys, Cookies>([
    PENDENCY_COOKIE_NAME,
    VIEWED_NOTIFICATION_COOKIE_NAME,
  ]);

  const userId = useMemo(() => getUserIdFromStorage(), []);

  const getPendency = useCallback(
    (farmId: number | null) => {
      if (farmId == null || userId == null) {
        return null;
      }

      const currentCookies = cookies[PENDENCY_COOKIE_NAME] ?? [];

      if (!Array.isArray(currentCookies)) {
        return null;
      }

      const item = currentCookies.find((item) =>
        item.includes(`${userId}|${farmId}|`),
      );

      const dateValue = Number(item?.split("|")[2]);
      return Number.isNaN(dateValue) ? null : dateValue;
    },
    [cookies, userId],
  );

  const setPendency = useCallback(
    (farmId: number | null, operationDate: number | null) => {
      if (userId == null || farmId == null) {
        return;
      }

      const currentCookies = cookies[PENDENCY_COOKIE_NAME] ?? [];
      const cookieData = `${userId}|${farmId}|${operationDate}`;

      if (!Array.isArray(currentCookies)) {
        setCookie(PENDENCY_COOKIE_NAME, [cookieData]);
        return;
      }

      const index = currentCookies.findIndex((item) =>
        item.includes(`${userId}|${farmId}|`),
      );

      if (index !== -1 && currentCookies[index] === cookieData) {
        return;
      }

      if (index !== -1) {
        const nextCookies = [...currentCookies];
        nextCookies[index] = cookieData;
        setCookie(PENDENCY_COOKIE_NAME, nextCookies);
      } else {
        setCookie(PENDENCY_COOKIE_NAME, [...currentCookies, cookieData]);
      }
    },
    [cookies, setCookie, userId],
  );

  const getNotificationsViewed = useCallback(() => {
    if (userId == null) {
      return [];
    }

    const currentCookies = cookies[VIEWED_NOTIFICATION_COOKIE_NAME] ?? [];

    if (!Array.isArray(currentCookies)) {
      return [];
    }

    const viewedNotifications = currentCookies
      .filter((item) => item.includes(userId.toString()))
      .map((item) => item.split("|")[1]);

    return viewedNotifications;
  }, [cookies, userId]);

  const setNotificationView = useCallback(
    (notificationId: string) => {
      if (userId == null) {
        return;
      }

      const currentCookies = cookies[VIEWED_NOTIFICATION_COOKIE_NAME] ?? [];
      const cookieData = `${userId}|${notificationId}|`;

      if (!Array.isArray(currentCookies)) {
        setCookie(VIEWED_NOTIFICATION_COOKIE_NAME, [cookieData]);
        return;
      }

      const index = currentCookies.findIndex((item) =>
        item.includes(cookieData),
      );

      if (index !== -1) {
        return;
      } else {
        setCookie(VIEWED_NOTIFICATION_COOKIE_NAME, [
          ...currentCookies,
          cookieData,
        ]);
      }
    },
    [cookies, setCookie, userId],
  );

  return [
    getPendency,
    setPendency,
    getNotificationsViewed,
    setNotificationView,
  ] as const;
}
